import { useMemo } from "react";
import { getChildrenErrors } from "../../../utils/conditionalErrorUtils";
/**
 * Hook for filtering fields based on search query and creating optimized error lookup map
 *
 * Returns:
 * - filteredFields: Fields matching search query (including parent/child relationships)
 * - fieldErrorMap: Map for O(1) error lookups by field name
 */
export var useFieldSearch = function useFieldSearch(_ref) {
  var fieldSchemas = _ref.fieldSchemas,
    fieldErrorsList = _ref.fieldErrorsList,
    searchQuery = _ref.searchQuery;
  // Note: fieldErrorsList is now passed as a prop from parent (calculated by useErrorManager hook)

  // All fields from schema (no longer filtered by sections status)
  var allFields = useMemo(function () {
    return fieldSchemas;
  }, [fieldSchemas]);

  // Track fields that directly matched the search query
  var directlyMatchedFields = useMemo(function () {
    var matched = new Set();
    if (!searchQuery) {
      return matched;
    }
    allFields.forEach(function (field) {
      if (field.label.toLowerCase().includes(searchQuery.toLowerCase()) || field.field_name.toLowerCase().includes(searchQuery.toLowerCase())) {
        matched.add(field.field_name);
      }
    });
    return matched;
  }, [allFields, searchQuery]);

  // Filter fields based on search query only (all fields always visible)
  var filteredFields = useMemo(function () {
    if (!searchQuery) {
      // No search query - show all fields (no visibility filtering)
      return allFields;
    }

    // When searching, show only matched fields and their parents/children
    var fieldsToInclude = new Set();
    var _addParentFields = function addParentFields(fieldName) {
      var _childError$condition;
      var childError = fieldErrorsList.find(function (err) {
        var _err$conditional_meta;
        return err.field === fieldName && err.type === "conditional" && ((_err$conditional_meta = err.conditional_metadata) === null || _err$conditional_meta === void 0 ? void 0 : _err$conditional_meta.field);
      });
      if (childError !== null && childError !== void 0 && (_childError$condition = childError.conditional_metadata) !== null && _childError$condition !== void 0 && _childError$condition.field) {
        var parentFieldName = childError.conditional_metadata.field;
        if (!fieldsToInclude.has(parentFieldName)) {
          fieldsToInclude.add(parentFieldName);
          _addParentFields(parentFieldName);
        }
      }
    };
    var _addChildFields = function addChildFields(fieldName) {
      var children = getChildrenErrors(fieldName, fieldErrorsList);
      children.forEach(function (childError) {
        if (!fieldsToInclude.has(childError.field) && directlyMatchedFields.has(childError.field)) {
          fieldsToInclude.add(childError.field);
          _addChildFields(childError.field);
        }
      });
    };
    allFields.forEach(function (field) {
      if (directlyMatchedFields.has(field.field_name)) {
        fieldsToInclude.add(field.field_name);
        _addParentFields(field.field_name);
        _addChildFields(field.field_name);
      }
    });
    return allFields.filter(function (field) {
      return fieldsToInclude.has(field.field_name);
    });
  }, [allFields, searchQuery, fieldErrorsList, directlyMatchedFields]);

  // Note: Auto-clear logic removed - fields are always visible now, so values are never auto-cleared

  // Create a map of field errors for quick lookup
  var fieldErrorMap = useMemo(function () {
    var map = new Map();
    fieldErrorsList.forEach(function (err) {
      map.set(err.field, err);
    });
    return map;
  }, [fieldErrorsList]);
  return {
    filteredFields: filteredFields,
    fieldErrorMap: fieldErrorMap
  };
};